import { CommonModule } from '@angular/common';
import {
  isHearingPanelPopulation,
  isCombinedVotingRound,
} from '@app/shared/workflow-voting/voting-sources';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import {
  VotingSettings,
  VotingRules,
  votingRoleChoices,
  VOTING_POPULATION,
  VOTING_ROUND,
} from '@app/shared/workflow-voting/voting.models';
import {
  WorkflowDefinitionDocument,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowCapabilityCatalogEntry } from './workflow-capability-authoring';
import {
  installVoting,
  votingProducers,
  applyVotingRoutes,
} from './workflow-voting-authoring';
import { createNoticeHearingField } from './workflow-notice-source';

@Component({
  selector: 'phms-workflow-voting-configuration',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective],
  templateUrl: './workflow-voting-configuration.component.html',
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }
      .voting-configuration {
        display: grid;
        gap: 0.7rem;
      }
      label {
        display: grid;
        gap: 0.25rem;
      }
      input,
      select {
        max-width: 100%;
        min-width: 0;
      }
      .check {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }
      fieldset {
        min-width: 0;
      }
      .error {
        color: var(--custom-status-danger-text-color);
      }
      .actions {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
    `,
  ],
})
export class WorkflowVotingConfigurationComponent implements OnChanges {
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input({ required: true }) nodeId!: string;
  @Input() code = 'voting.population';
  @Input() capabilities: WorkflowCapabilityCatalogEntry[] = [];
  @Input() disabled = false;
  @Input() deferred = false;
  @Input() provider = '';
  @Input() editorKey = '';
  @Input() initialRequirement: Record<string, unknown> | null = null;
  @Output() changed = new EventEmitter<void>();
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  readonly roleChoices = votingRoleChoices;
  readonly outcomeChoices = [
    'approved',
    'rejected',
    'no_decision',
    'quorum_not_met',
  ];
  populationSource = '';
  roundSource = '';
  sourceKind: 'manual' | 'hearing-panel' = 'manual';
  hearingField = '';
  roles: string[] = [];
  question = '';
  captureMode: VotingSettings['captureMode'] = 'personal';
  rules: VotingRules = {
    quorumThreshold: 0,
    approvalRuleCode: '',
    denominatorCode: '',
    abstentionQuorumCode: '',
    tieCode: '',
  };
  allMembers = false;
  outcomes: string[] = [];
  error = '';
  routeOutcomes = false;
  existingRouter = false;
  routes: Record<string, string> = {};
  private context = '';
  private original = '';
  get populations() {
    return votingProducers(this.document, this.nodeId, 'voting.population');
  }
  get rounds() {
    return votingProducers(this.document, this.nodeId, 'voting.ballots');
  }
  get hears() {
    return (
      this.document.processData?.fields.filter(
        (f) =>
          f.type === 'record-reference' &&
          f.recordTypeCode === 'scheduled-proceeding',
      ) ?? []
    );
  }
  get configureRound() {
    return (
      this.code === 'voting.ballots' ||
      (this.code === 'voting.result' && this.roundSource === this.nodeId)
    );
  }
  get configurePopulation() {
    return (
      !this.integratedPopulation &&
      (this.code === 'voting.population' ||
        (this.configureRound && this.populationSource === this.nodeId))
    );
  }
  get integratedPopulation(): boolean {
    return (
      this.document.nodes
        .find((n) => n.id === this.nodeId)
        ?.declaration?.detail.blocks.some(isHearingPanelPopulation) === true
    );
  }
  get combinedRound(): boolean {
    return (
      this.document.nodes
        .find((n) => n.id === this.nodeId)
        ?.declaration?.detail.blocks.some(isCombinedVotingRound) === true
    );
  }
  label(id: string) {
    const n = this.document.nodes.find((n) => n.id === id);
    return (
      n?.displayName ||
      n?.stepCode ||
      (n?.typeCode === 'terminal'
        ? 'Complete workflow'
        : id === this.nodeId
          ? 'This step'
          : n?.id || 'Unavailable action')
    );
  }
  private fingerprint(doc: WorkflowDefinitionDocument) {
    return JSON.stringify({
      fields: doc.processData?.fields,
      sources: doc.nodes.map((n) => ({
        id: n.id,
        blocks: n.declaration?.detail.blocks.filter(
          (b) => b.provider === 'voting',
        ),
      })),
      edges: doc.edges,
    });
  }
  ngOnChanges() {
    const context =
      this.nodeId +
      '/' +
      this.code +
      '/' +
      this.provider +
      '/' +
      this.editorKey;
    if (this.context === context) return;
    this.context = context;
    this.original = this.fingerprint(this.document);
    const node = this.document.nodes.find((n) => n.id === this.nodeId);
    const block = (code: string) =>
      node?.declaration?.detail.blocks.find(
        (b) => b.provider === 'voting' && b.code === code,
      )?.settings as unknown as VotingSettings | undefined;
    const pop = block('voting.population'),
      ballot = block('voting.ballots'),
      result = block('voting.result');
    this.populationSource =
      ballot?.populationNodeId ||
      (this.populations.length === 1
        ? this.populations[0].id
        : this.populations.length
          ? ''
          : this.nodeId);
    this.roundSource = String(
      this.initialRequirement?.['sourceNodeId'] ||
        result?.roundNodeId ||
        (this.rounds.length === 1
          ? this.rounds[0].id
          : this.rounds.length
            ? ''
            : this.nodeId),
    );
    this.sourceKind = pop?.sourceKind ?? 'manual';
    this.hearingField =
      pop?.hearingReferenceField ||
      (this.hears.length === 1 ? this.hears[0].key : '');
    this.question = ballot?.question ?? '';
    this.captureMode = ballot?.captureMode ?? 'personal';
    try {
      this.roles = JSON.parse(pop?.votingRolesJson || '[]');
      if (ballot?.rulesJson) this.rules = JSON.parse(ballot.rulesJson);
    } catch {
      this.error = 'The saved voting settings are unavailable.';
    }
    this.allMembers = this.initialRequirement?.['allMembers'] === true;
    this.outcomes = Array.isArray(this.initialRequirement?.['outcomeCodes'])
      ? [...(this.initialRequirement!['outcomeCodes'] as string[])]
      : [];
    const router = this.document.nodes.find(
      (n) =>
        n.id === `${this.nodeId}-voting-result` &&
        n.routing?.conditionCode === 'voting.result',
    );
    this.existingRouter = !!router;
    this.routeOutcomes = !!router;
    this.routes = Object.fromEntries(
      this.document.edges
        .filter((e) => e.fromNodeId === router?.id && e.outcomeCode)
        .map((e) => [e.outcomeCode!, e.toNodeId]),
    );
  }
  edit() {
    this.error = '';
    this.changed.emit();
  }
  toggle(list: 'roles' | 'outcomes', value: string, checked: boolean) {
    this[list] = checked
      ? [...new Set([...this[list], value])]
      : this[list].filter((v) => v !== value);
    this.edit();
  }
  get requirementSettings() {
    return {
      sourceNodeId:
        this.provider === VOTING_POPULATION
          ? this.nodeId
          : this.code === 'voting.ballots'
            ? this.nodeId
            : this.roundSource,
      allMembers: this.allMembers,
      ...(this.provider === 'workflow.quorum-evaluated'
        ? { outcomeCodes: this.outcomes }
        : {}),
    };
  }
  /** Raw deferred voting input is part of its owning requirement buffer. */
  pendingInputFingerprint(): string {
    return JSON.stringify([this.original, this.fingerprint(this.document), this.code, this.provider, this.editorKey,
      this.populationSource, this.roundSource, this.sourceKind, this.hearingField, this.roles, this.question,
      this.captureMode, this.rules, this.allMembers, this.outcomes, this.routeOutcomes, this.routes,
      this.capabilities, this.disabled]);
  }
  issue(): string {
    if (
      this.configurePopulation &&
      this.sourceKind === 'hearing-panel' &&
      !this.roles.length
    )
      return 'Choose the panel roles eligible to vote.';
    if (
      this.configurePopulation &&
      this.sourceKind === 'hearing-panel' &&
      this.hears.length > 1 &&
      !this.hearingField
    )
      return 'Choose the hearing source for the panel.';
    if (this.configureRound) {
      if (!this.populationSource)
        return 'Choose a confirmed population action or define one on this step.';
      if (!this.question.trim() || this.question.length > 1000)
        return 'Enter a voting question of up to 1000 characters.';
      if (
        !Number.isInteger(Number(this.rules.quorumThreshold)) ||
        Number(this.rules.quorumThreshold) < 1 ||
        Number(this.rules.quorumThreshold) > 256
      )
        return 'Choose a quorum threshold from 1 to 256.';
      if (
        !['strict_majority', 'unanimity'].includes(
          this.rules.approvalRuleCode,
        ) ||
        !['eligible_members', 'votes_cast'].includes(
          this.rules.denominatorCode,
        ) ||
        !['count', 'exclude'].includes(this.rules.abstentionQuorumCode) ||
        !['no_decision', 'reject'].includes(this.rules.tieCode)
      )
        return 'Choose explicit approval, denominator, abstention and tie rules.';
    }
    if (this.code === 'voting.result' && !this.roundSource)
      return 'Choose the voting round action.';
    if (this.provider === 'workflow.quorum-evaluated' && !this.outcomes.length)
      return 'Choose which quorum results permit completion.';
    if (this.routeOutcomes && this.outcomeChoices.some((o) => !this.routes[o]))
      return 'Choose a destination for each voting outcome.';
    return '';
  }
  apply(doc = this.document): WorkflowDefinitionDocument {
    return this.previewPendingDocument(doc);
  }
  /** Build and validate deferred authoring changes without emitting or changing input. */
  previewPendingDocument(doc = this.document): WorkflowDefinitionDocument {
    if (this.fingerprint(doc) !== this.original)
      throw new Error(
        'Voting sources changed while this editor was open. Cancel and reopen to review them.',
      );
    const issue = this.issue();
    if (issue) throw new Error(issue);
    let next = doc;
    if (this.configurePopulation) {
      let field = this.hearingField;
      if (
        this.sourceKind === 'hearing-panel' &&
        doc.subjectTypeCode !== 'hearing_cycle' &&
        !this.hears.length
      ) {
        const created = createNoticeHearingField(next);
        field = created.key;
        next = {
          ...next,
          payloadSchemaVersion: requireWorkflowPayloadVersion(
            next.payloadSchemaVersion,
            '1.10',
          ),
          processData: {
            ...next.processData,
            fields: [...(next.processData?.fields ?? []), created],
          },
        };
      }
      next = installVoting(
        next,
        this.nodeId,
        'voting.population',
        {
          sourceKind: this.sourceKind,
          captureMode: 'personal',
          ...(field && this.sourceKind === 'hearing-panel'
            ? { hearingReferenceField: field }
            : {}),
          ...(this.sourceKind === 'hearing-panel'
            ? { votingRolesJson: JSON.stringify(this.roles) }
            : {}),
        },
        this.capabilities,
      );
    }
    if (this.configureRound)
      next = installVoting(
        next,
        this.nodeId,
        'voting.ballots',
        {
          sourceKind: 'manual',
          captureMode: this.captureMode,
          populationNodeId: this.populationSource,
          question: this.question.trim(),
          rulesJson: JSON.stringify({
            ...this.rules,
            quorumThreshold: Number(this.rules.quorumThreshold),
          }),
        },
        this.capabilities,
      );
    if (
      this.code !== 'voting.population' &&
      !this.combinedRound &&
      (this.provider !== VOTING_ROUND || this.routeOutcomes)
    )
      next = installVoting(
        next,
        this.nodeId,
        'voting.result',
        {
          sourceKind: 'manual',
          captureMode: 'personal',
          roundNodeId:
            this.code === 'voting.ballots' ? this.nodeId : this.roundSource,
        },
        this.capabilities,
      );
    if (this.code !== 'voting.population' && this.routeOutcomes)
      next = applyVotingRoutes(next, this.nodeId, this.routes);
    return next;
  }
  save() {
    if (this.disabled) return;
    try {
      const next = this.apply();
      this.documentChange.emit(next);
      this.original = this.fingerprint(next);
    } catch (e) {
      this.error =
        e instanceof Error
          ? e.message
          : 'The voting configuration could not be applied.';
    }
  }
}
